import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/Login.css';
import PublicNav from '../PublicNav';
import LoginModal from './LoginModal';

function ClubLogin({ onLogin }) {
  const navigate = useNavigate();
  const [veranstaltungen, setVeranstaltungen] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [detailModal, setDetailModal] = useState(null);

  useEffect(() => { fetchVeranstaltungen(); }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') { setShowLoginModal(false); setDetailModal(null); }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  const fetchVeranstaltungen = async () => {
    try {
      const [turniereRes, eventsRes] = await Promise.all([
        fetch('/api/turniere/public'),
        fetch('/api/events/public')
      ]);
      const turniereData = turniereRes.ok ? await turniereRes.json() : { success: true, data: [] };
      const eventsData = eventsRes.ok ? await eventsRes.json() : { success: true, data: [] };
      const combined = [
        ...(turniereData.success ? turniereData.data : Array.isArray(turniereData) ? turniereData : []).map(t => ({ ...t, _typ: 'turnier', _id: t.turnier_id || t.id })),
        ...(eventsData.success ? eventsData.data : Array.isArray(eventsData) ? eventsData : []).map(e => ({ ...e, _typ: 'event', _id: e.event_id }))
      ].sort((a, b) => new Date(a.datum) - new Date(b.datum));
      setVeranstaltungen(combined);
    } catch (err) {
      console.error('Fehler beim Laden:', err);
    } finally {
      setListLoading(false);
    }
  };

  const openLoginForItem = (item) => {
    setSelectedItem(item);
    setDetailModal(null);
    setShowLoginModal(true);
  };

  const openDetailMenu = (e, item) => {
    e.stopPropagation();
    setDetailModal({ item, view: 'menu' });
  };

  const getTags = (item) => {
    const src = item.kategorien || item.disziplin || '';
    return src ? src.split(',').map(s => s.trim()).filter(Boolean) : [];
  };

  return (
    <div className="login-page-new">
      <div className="login-main">

        {/* Nav */}
        <PublicNav onLogin={() => { setSelectedItem(null); setShowLoginModal(true); }} />

        {/* Kommende Veranstaltungen — sofort sichtbar */}
        <div className="posters-page-container">
          <div className="section-header">
            <h1>Kommende Veranstaltungen</h1>
            <p>Plakat anklicken zum Anmelden</p>
          </div>

          {listLoading ? (
            <div className="loading-state"><div className="spinner" /><p>Lade Veranstaltungen...</p></div>
          ) : veranstaltungen.length === 0 ? (
            <div className="empty-state"><p>Aktuell sind keine Veranstaltungen verfügbar.</p></div>
          ) : (
            <div className="tournaments-grid-login">
              {veranstaltungen.map(item => {
                const key = `${item._typ}-${item._id}`;
                const isSelected = selectedItem?._id === item._id && selectedItem?._typ === item._typ;
                return (
                  <div
                    key={key}
                    className={`tournament-card-login ${isSelected ? 'selected' : ''}`}
                    onClick={() => openLoginForItem(item)}
                  >
                    <img
                      src={item.plakat_url || item.bild || ''}
                      alt={item.name}
                      className="tournament-card-poster-img"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                    {isSelected && <div className="tournament-card-check">✓</div>}
                    <div className="poster-strip" onClick={e => e.stopPropagation()}>
                      <span className="poster-strip-name">{item.name}</span>
                      <button className="poster-menu-btn" onClick={(e) => openDetailMenu(e, item)} aria-label="Optionen">
                        ⋯
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Feature Banner — darunter als Info-Sektion */}
        <div className="login-feature-banner">
          <img src="/platform-features-v2.webp" alt="TDA Tournament Software" className="login-feature-banner-img" />
        </div>
      </div>

      {/* Detail / Options Modal */}
      {detailModal && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setDetailModal(null); }}>
          <div className="modal-box detail-modal-box">
            <button className="modal-close" onClick={() => setDetailModal(null)}>✕</button>
            <div className="detail-modal-header">
              {detailModal.view !== 'menu' && (
                <button className="detail-back-btn" onClick={() => setDetailModal({ ...detailModal, view: 'menu' })}>←</button>
              )}
              <div className="detail-modal-title">
                <span className={`detail-typ-badge detail-typ-badge--${detailModal.item._typ}`}>
                  {detailModal.item._typ === 'turnier' ? 'Turnier' : 'Event'}
                </span>
                <h3>{detailModal.item.name}</h3>
                {detailModal.item.datum && (
                  <p className="detail-modal-date">
                    {new Date(detailModal.item.datum).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}
                    {detailModal.item.ort ? ` · ${detailModal.item.ort}` : ''}
                  </p>
                )}
              </div>
            </div>

            {detailModal.view === 'menu' && (
              <div className="detail-menu-list">
                <button className="detail-menu-item" onClick={() => setDetailModal({ ...detailModal, view: 'info' })}>
                  <span className="detail-menu-icon">ℹ</span>
                  <span className="detail-menu-label">Info</span>
                  <span className="detail-menu-arrow">›</span>
                </button>
                <button className="detail-menu-item" onClick={() => setDetailModal({ ...detailModal, view: 'divisions' })}>
                  <span className="detail-menu-icon">⊞</span>
                  <span className="detail-menu-label">Divisionen & Preis</span>
                  <span className="detail-menu-arrow">›</span>
                </button>
                <button className="detail-menu-item detail-menu-item--cta" onClick={() => openLoginForItem(detailModal.item)}>
                  <span className="detail-menu-icon">▶</span>
                  <span className="detail-menu-label">Anmelden</span>
                </button>
              </div>
            )}

            {detailModal.view === 'info' && (
              <div className="detail-content">
                <p className="detail-section-label">Kategorien</p>
                {getTags(detailModal.item).length > 0 ? (
                  <div className="detail-tags">
                    {getTags(detailModal.item).map(tag => <span key={tag} className="detail-tag">{tag}</span>)}
                  </div>
                ) : (
                  <p className="detail-empty">Keine Kategorien hinterlegt.</p>
                )}
              </div>
            )}

            {detailModal.view === 'divisions' && (
              <div className="detail-content">
                <p className="detail-section-label">Divisionen</p>
                {getTags(detailModal.item).length > 0 ? (
                  <div className="detail-tags">
                    {getTags(detailModal.item).map(d => <span key={d} className="detail-tag">{d}</span>)}
                  </div>
                ) : (
                  <p className="detail-empty">Keine Divisionen hinterlegt.</p>
                )}
                {detailModal.item.preis > 0 && (
                  <>
                    <p className="detail-section-label" style={{ marginTop: '1.5rem' }}>Preis</p>
                    <div className="detail-price">
                      {parseFloat(detailModal.item.preis).toFixed(2)} €
                      <span className="detail-price-label">pro Teilnehmer</span>
                    </div>
                  </>
                )}
                <button className="detail-anmelden-btn" onClick={() => openLoginForItem(detailModal.item)}>
                  Jetzt anmelden
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={onLogin}
        selectedItem={selectedItem}
      />
    </div>
  );
}

export default ClubLogin;
